import { createContext, useContext } from 'react';

import type { AccountApi, Me } from './accountApi';

/**
 * Who is logged in, as the API sees them: `GET /me`, asked once per login.
 *
 * The callback page, the onboarding gate, the Account page and the sign-up
 * questions all need to know whether onboarding is done. One answer, shared,
 * so finishing the questions is seen everywhere at once.
 */
export type MeStatus =
  /** AuthKit is still finding out whether anyone is logged in. */
  | { kind: 'auth-loading' }
  | { kind: 'logged-out' }
  | { kind: 'loading' }
  | { kind: 'ready'; me: Me }
  /** The API could not be reached or refused. The site carries on without it. */
  | { kind: 'error'; message: string };

export interface MeContextValue {
  status: MeStatus;
  api: AccountApi;
  /** Asks again, after an error or a change made elsewhere. */
  refresh: () => void;
  /** Onboarding just finished here: no need to ask the server to know it. */
  markOnboardingComplete: () => void;
}

export const MeContext = createContext<MeContextValue | null>(null);

export function useMe(): MeContextValue {
  const value = useContext(MeContext);
  if (!value) throw new Error('useMe must be used inside MeProvider');
  return value;
}
